import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Column as RdgColumn, SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { EditFileBlueIcon, RoundedPlusSignIcon } from "../../assets";
import {
  InteractionObjectType,
  InteractionType,
  LocalStorageConstants,
  OrganisationTypeConstants,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../constants";
import { CursorChangeProps, DataGridColumnCurrency, SelectData } from "../../models";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import {
  GetProjectIssuancesResponse,
  searchInteractions,
  searchIssuanceTransactions,
  SearchIssuanceTransactionsRequest,
  SearchIssuanceTransactionsResponse,
} from "../../service/query";
import { FilterGroups, ResultData, Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { flattenObject, jsonReviver, recursivelyValidateAndTransformDateToString } from "../../utils";
import {
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
  Toast,
} from "../../widget";
import { EditColumns, IconCellFormatterData } from "../../widget/data/DataGrid/models";
import { UnitTransactionsPanelFilterObjects } from "./UnitTransactionsPanel";
import { buildCompanyCell } from "./utils";

interface UseUnitTransactionsPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  filter?: UnitTransactionsPanelFilterObjects;
  canEdit: boolean;
  handleRefresh: () => void;
}

interface UseUnitTransactionsPanelReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;

  showActionsMenu: boolean;
  setShowActionsMenu: Dispatch<SetStateAction<boolean>>;

  showEditIssuanceTransactionPanel: boolean;
  showAddIssuanceTransactionPanel: boolean;
  showProgressIssuanceTransactionPanel: boolean;
  setShowAddIssuanceTransactionPanel: Dispatch<SetStateAction<boolean>>;
  handleCloseIssuanceTransactionPanel: () => void;

  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0] | undefined;
  showEditColumnsPanel: boolean;
  setShowEditColumnsPanel: Dispatch<SetStateAction<boolean>>;
  actionsMenuOptionsList: ProfileMenuItemType[];
  exportFileName: string;
  defaultEditColumns: EditColumns;
  inventoryTransactionsEditColumnsLocalStorageItemName: string;
  exportTable: boolean | undefined;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];
  refreshIssuanceTransactions: boolean;
  handleRefreshIssuancesAndIssuanceTransactions: () => void;

  tagsFilterSelectData: SelectData;
  selectedTagKeys: string[];
  onTagsFilter: (keys: string[]) => Promise<void>;
  projectName: string | undefined;
  portalVariant: "hub" | "seed";
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "transactionDate", direction: "ASC" }];

/*
  How many of the issuance's transactions the tag filter reads to work out which tags to offer. Matches
  searchIssuanceTransactions' server-side MaxPageSize, so it is as much as one request can return.
*/
const TAG_SCOPE_TRANSACTIONS_LIMIT = 1000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Transaction date",
    key: "transactionDate",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "left",
    sortable: true,
    filterable: false,
  },
  {
    name: "Counterparty",
    key: "counterParty",
    dataType: "string",
    formatter: "buttonLink",
    filterable: true,
    sortable: true,
  },
  {
    name: "Broker",
    key: "broker",
    dataType: "string",
    formatter: "buttonLink",
    filterable: true,
    sortable: true,
  },
  {
    name: "Type",
    key: "type",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Quantity",
    key: "quantity",
    dataType: "number",
    formatter: "number",
    sortable: true,
    filterable: true,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Fees",
    key: "fees",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Total value",
    key: "totalValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Transaction reference",
    key: "externalReference",
    dataType: "string",
    formatter: "stringWithEllipsis",
    sortable: true,
    filterable: true,
  },
  {
    name: "Progress",
    key: "linkIcon",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    minWidth: 100,
    sortable: false,
  },
  {
    name: "Edit",
    key: "editIcon",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    minWidth: 100,
    sortable: false,
  },
  {
    name: "ID",
    key: "uuid",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: false,
    sortable: false,
  },
  {
    name: "Previous transaction ID",
    key: "parentTransactionUuid",
    dataType: "number",
    filterable: false,
    sortable: false,
  },
  {
    name: "Unit size (Ha or Km)",
    key: "areaHa",
    dataType: "number",
    filterable: true,
    sortable: true,
  },
  {
    name: "Created at",
    key: "createdAt",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Updated at",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Created by",
    key: "createdByUser.fullName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Updated by",
    key: "updatedByUser.fullName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
];

/*
  The Progress and Edit icon columns are write affordances, so a viewer without MANAGE_PROJECT_ISSUANCES gets
  neither the icons (see `formatData`) nor the columns that would otherwise render empty and be offered in the
  Edit columns panel.
*/
const getVisibleColumns = (canEdit: boolean): typeof columns =>
  canEdit ? columns : columns.filter((col) => col.key !== "editIcon" && col.key !== "linkIcon");

const getEditColumns = (canEdit: boolean): EditColumns => {
  const editColumns = getVisibleColumns(canEdit).reduce((acc: EditColumns, col, index) => {
    let show = true;
    if (
      col.key === "createdAt" ||
      col.key === "updatedAt" ||
      col.key === "createdByUser.fullName" ||
      col.key === "updatedByUser.fullName" ||
      col.key === "uuid" ||
      col.key === "parentTransactionUuid" ||
      col.key === "areaHa"
    )
      show = false;
    acc[col.key] = { show, index };
    return acc;
  }, {});

  return editColumns;
};

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.linkIcon !== undefined) delete row.linkIcon;
    // eslint-disable-next-line no-param-reassign
    if (row.editIcon !== undefined) delete row.editIcon;
    // The company cells hold a link object - export the name they display, not the object.
    // eslint-disable-next-line no-param-reassign
    if (row.counterParty !== undefined) row.counterParty = (row.counterParty as { text?: string })?.text ?? "";
    // eslint-disable-next-line no-param-reassign
    if (row.broker !== undefined) row.broker = (row.broker as { text?: string })?.text ?? "";
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "editIcon" && column.key !== "linkIcon");
};

export const useUnitTransactionsPanel = ({
  issuance,
  filter,
  canEdit,
  // eslint-disable-next-line unused-imports/no-unused-vars
  handleRefresh,
}: UseUnitTransactionsPanelProps): UseUnitTransactionsPanelReturnData => {
  const { user, currentOrganisationUuid, currentUserType } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const navigate = useNavigate();
  const [dataIsLoading, setDataIsLoading] = useState(true);
  /*
    Filter, sort, paging and refresh changes each start a load, so two can be in flight at once.
    Stamp them so only the newest clears the loading flag.
  */
  const loadRequestIdRef = useRef(0);

  const [exportTable, setExportTable] = useState<boolean>();

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditColumnsPanel, setShowEditColumnsPanel] = useState(false);
  const [showAddIssuanceTransactionPanel, setShowAddIssuanceTransactionPanel] = useState(false);
  const [showEditIssuanceTransactionPanel, setShowEditIssuanceTransactionPanel] = useState(false);
  const [showProgressIssuanceTransactionPanel, setShowProgressIssuanceTransactionPanel] = useState(false);

  const [issuanceTransaction, setIssuanceTransaction] = useState<SearchIssuanceTransactionsResponse["results"][0]>();

  // Memoised so the grid is not handed a new column array on every render.
  const visibleColumns = useMemo(() => getVisibleColumns(canEdit), [canEdit]);

  const [refreshIssuanceTransactions, setRefreshIssuanceTransactions] = useState(false);
  /*
    Bumped whenever a tag may have changed. A counter rather than a toggle because saving a transaction
    raises it from the refresh and the close handler in the same batch, and two toggles cancel out.
  */
  const [tagOptionsRefreshKey, setTagOptionsRefreshKey] = useState(0);
  const [tagsFilter, setTagsFilter] = useState<string[]>();
  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);

  /*
    Read by the tag-options effect to prune a selection the options no longer contain. Held in a ref so
    the effect does not re-read the options every time the selection changes.
  */
  const selectedTagKeysRef = useRef(selectedTagKeys);
  useEffect(() => {
    selectedTagKeysRef.current = selectedTagKeys;
  }, [selectedTagKeys]);

  // The transaction changed, so both the grid and the tags it carries are stale.
  const handleRefreshIssuancesAndIssuanceTransactions = (): void => {
    handleRefresh();
    setRefreshIssuanceTransactions((prev) => !prev);
    setTagOptionsRefreshKey((prev) => prev + 1);
  };

  const handleCloseIssuanceTransactionPanel = (): void => {
    setShowAddIssuanceTransactionPanel(false);
    setShowEditIssuanceTransactionPanel(false);
    setShowProgressIssuanceTransactionPanel(false);
    setIssuanceTransaction(undefined);
    setSelectedTagKeys([]);
    setTagsFilter(undefined);
    /*
      Tags are committed by the tags flyout itself rather than by the transaction save, so they may have
      changed even when the panel is cancelled - but no grid row has, so only the options are re-read.
    */
    setTagOptionsRefreshKey((prev) => prev + 1);
  };

  const actionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit columns",
      action: () => setShowEditColumnsPanel(true),
    },
    {
      id: 2,
      value: "Export table",
      action: () => setExportTable(!exportTable),
    },
  ];

  const formatData = useCallback(
    (responseData: SearchIssuanceTransactionsResponse | undefined): ResultData[] => {
      return (
        responseData?.results?.map((d) => {
          const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };

          result.counterParty = buildCompanyCell(d.counterParty, currentUserType, navigate);
          result.broker = buildCompanyCell(d.broker, currentUserType, navigate);

          if (
            canEdit &&
            d.type !== ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED &&
            issuance?.status !== ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE
          ) {
            result.linkIcon = {
              icon: <RoundedPlusSignIcon className="ProgressIssuanceIcon" />,
              action: () => {
                setIssuanceTransaction(d);
                setShowProgressIssuanceTransactionPanel(true);
              },
            } as IconCellFormatterData;
          }

          if (canEdit) {
            result.editIcon = {
              icon: <EditFileBlueIcon width={20} height={20} />,
              action: () => {
                setIssuanceTransaction(d);
                setShowEditIssuanceTransactionPanel(true);
              },
            } as IconCellFormatterData;
          }

          return result;
        }) || []
      );
    },
    [currentUserType, navigate, issuance?.status, canEdit]
  );

  // Resolves the selected tags into the transaction uuids the grid filters on.
  const resolveTagsFilter = useCallback(async (keys: string[]): Promise<void> => {
    if (keys.length === 0) {
      setTagsFilter(undefined);
      setRefreshIssuanceTransactions((prev) => !prev);
      return;
    }

    const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

    searchInteractionsFilterCriteria.uuid = {
      operator: "in",
      value: keys,
    };

    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: searchInteractionsFilterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilter(
          response.data.results
            .filter((x) => x.objectInteractions.length > 0)
            .map((x) => x.objectInteractions.map((y) => y.objectUuid))
            .flat()
        );
      } else {
        setTagsFilter(undefined);
      }
      setRefreshIssuanceTransactions((prev) => !prev);
    });
  }, []);

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    setSelectedTagKeys(keys);
    await resolveTagsFilter(keys);
  };

  const onChange = async ({
    filtering,
    paging,
    sorting,
  }: CursorChangeProps): Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> => {
    loadRequestIdRef.current += 1;
    const requestId = loadRequestIdRef.current;
    setDataIsLoading(true);

    let data = {
      resultData: [] as ResultData[],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    // The company columns display a link cell but filter on the company's name.
    if (filterCriteria.counterParty) {
      filterCriteria.counterParty = { name: filterCriteria.counterParty };
    }

    if (filterCriteria.broker) {
      filterCriteria.broker = { name: filterCriteria.broker };
    }

    if (tagsFilter && tagsFilter.length > 0) {
      filterCriteria.uuid = {
        operator: "in",
        value: tagsFilter,
      };
    }

    if (issuance?.uuid) {
      filterCriteria.issuanceUuid = {
        operator: "eq",
        value: issuance.uuid,
      };
    }

    if (filter?.projectUuid) {
      filterCriteria.project = {
        uuid: {
          operator: "eq",
          value: filter.projectUuid,
        },
      };
    }

    if (filter?.vintage) {
      filterCriteria.vintage = {
        operator: "eq",
        value: filter.vintage,
      };
    }

    if (filter?.projectTypeDisplayName) {
      filterCriteria.project = {
        projectType: {
          displayName: {
            operator: "eq",
            value: filter.projectTypeDisplayName,
          },
        },
      };
    }

    if (filter?.quantityType) {
      filterCriteria.quantityType = {
        operator: "eq",
        value: filter.quantityType,
      };
    }

    if (filter?.habitatCondition) {
      filterCriteria.habitatCondition = {
        operator: "eq",
        value: filter.habitatCondition,
      };
    }

    if (filter?.habitatGroup) {
      filterCriteria.habitatGroup = {
        operator: "eq",
        value: filter.habitatGroup,
      };
    }

    if (filter?.distinctiveness) {
      filterCriteria.distinctiveness = {
        operator: "eq",
        value: filter.distinctiveness,
      };
    }

    if (filter?.lpa !== undefined) {
      filterCriteria.lpa = {
        operator: "eq",
        value: filter.lpa,
      };
    }

    if (filter?.nca !== undefined) {
      filterCriteria.nca = {
        operator: "eq",
        value: filter.nca,
      };
    }

    if (filter?.lnrs !== undefined) {
      filterCriteria.lnrs = {
        operator: "eq",
        value: filter.lnrs,
      };
    }

    if (filter?.designatedSite !== undefined) {
      filterCriteria.designatedSite = {
        operator: "eq",
        value: filter.designatedSite,
      };
    }

    const sortKeyMap: Record<string, string> = {
      counterParty: "counterParty.name",
      broker: "broker.name",
    };

    const request: SearchIssuanceTransactionsRequest & FilterGroups<SearchIssuanceTransactionsRequest["filter"]> = {
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    };

    /*
      A company can be either side of a deal, so its two criteria are an OR group of their own rather than
      criteria on the main filter, which stays an AND of everything else the panel is scoped by.
    */
    if (filter?.companyUuid) {
      const companyFilterCriteria = dataGridMapFilterCriteria([]);
      companyFilterCriteria.counterParty = {
        uuid: {
          operator: "eq",
          value: filter.companyUuid,
        },
      };
      companyFilterCriteria.broker = {
        uuid: {
          operator: "eq",
          value: filter.companyUuid,
        },
      };

      request.filterGroups = [
        {
          operator: "or",
          filter: { results: companyFilterCriteria },
        },
      ];
    }

    await searchIssuanceTransactions(request)
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        // A superseded response must not report the grid as loaded while a newer request is in flight.
        if (requestId === loadRequestIdRef.current) {
          setDataIsLoading(false);
        }
      });

    return data;
  };

  /*
    The tags the filter offers, which are the tags carried by the transactions the panel is showing.
    Scoping needs those transactions' uuids first: a tag's links can only be matched by a specific
    objectUuid, so there is no single filter for "tags on any transaction of this issuance".
  */
  const getScopedTags = useCallback(
    async (organisationUuid: string): Promise<SelectData> => {
      const objectInteractionCriteria = dataGridMapFilterCriteria([]);
      objectInteractionCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ISSUANCE_TRANSACTION,
      };
      /*
        Removing a tag is a soft delete that stamps end_date on the link rather than deleting it, so
        without this a tag that has been removed from every transaction stays in the list for good.
      */
      objectInteractionCriteria.endDate = {
        operator: "eq",
        value: null,
      };

      if (issuance?.uuid) {
        const transactionsFilterCriteria = dataGridMapFilterCriteria([]);
        transactionsFilterCriteria.issuanceUuid = {
          operator: "eq",
          value: issuance.uuid,
        };

        const transactionsResponse = await searchIssuanceTransactions({
          paging: {
            beforeCursor: null,
            afterCursor: null,
            limit: TAG_SCOPE_TRANSACTIONS_LIMIT,
          },
          filter: { results: transactionsFilterCriteria },
        });

        if (transactionsResponse.status === Status.Error) {
          throw new Error("Unable to read the issuance's transactions");
        }

        const transactionUuids = transactionsResponse.data?.results.map((t) => t.uuid) ?? [];

        // An issuance with no transactions has no tags to filter by, so there is nothing to ask for.
        if (transactionUuids.length === 0) return [];

        objectInteractionCriteria.objectUuid = {
          operator: "in",
          value: transactionUuids,
        };
      }

      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: organisationUuid,
      };
      filterCriteria.objectInteractions = { some: objectInteractionCriteria };

      const response = await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      });

      if (response.status === Status.Error) {
        throw new Error("Unable to read the organisation's tags");
      }

      return (response.data?.results ?? []).map((x) => ({
        key: x.uuid,
        value: JSON.parse(x.data, jsonReviver).name,
      })) as SelectData;
    },
    [issuance?.uuid]
  );

  useEffect(() => {
    if (!currentOrganisationUuid) return undefined;

    let cancelled = false;

    getScopedTags(currentOrganisationUuid)
      .then((tags) => {
        if (cancelled) return;

        // Set unconditionally - an empty response has to clear the list, not leave the previous one up.
        setTagsFilterSelectData(tags);

        /*
          A tag that has just lost its last live link is gone from the options but may still be
          selected, which would otherwise keep narrowing the grid by a tag the user can no longer see.
        */
        const availableKeys = new Set(tags.map((tag) => tag.key));
        const stillSelected = selectedTagKeysRef.current.filter((key) => availableKeys.has(key));

        if (stillSelected.length !== selectedTagKeysRef.current.length) {
          setSelectedTagKeys(stillSelected);
          resolveTagsFilter(stillSelected).catch(() => {
            Toast.error({ message: "Unable to update the tag filter" });
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        Toast.error({ message: "Unable to load the tag filter" });
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrganisationUuid, tagOptionsRefreshKey, getScopedTags, resolveTagsFilter]);

  return {
    columns: visibleColumns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
    issuanceTransaction,
    actionsMenuOptionsList,
    showEditIssuanceTransactionPanel,
    showAddIssuanceTransactionPanel,
    showProgressIssuanceTransactionPanel,
    showActionsMenu,
    showEditColumnsPanel,
    handleCloseIssuanceTransactionPanel,
    setShowActionsMenu,
    setShowAddIssuanceTransactionPanel,
    setShowEditColumnsPanel,
    exportTable,
    filterExportColumns,
    filterExportRows,
    defaultEditColumns: getEditColumns(canEdit),
    exportFileName: `${issuance?.uuid ? `${issuance.uuid}_` : ""}Inventory_Unit_Transactions_$`.replaceAll(" ", "_"),
    inventoryTransactionsEditColumnsLocalStorageItemName: `${user?.userUuid ? `${user?.userUuid}_` : ""}${LocalStorageConstants.PROJECT_INVENTORY_UNIT_TRANSACTIONS_PREFERENCES}`,
    refreshIssuanceTransactions,
    handleRefreshIssuancesAndIssuanceTransactions,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    projectName: projectDetails?.displayName,
    portalVariant: currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "hub",
  };
};
